import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Monogram, Wordmark } from "../components/brand/Logo";
import { LogoLockup } from "../components/brand/LogoLockup";
import { Button } from "../components/brand/Button";
import { CinematicCanvas, MonogramWatermark, Eyebrow, Reveal, DrawRule, CornerBrackets } from "../components/brand/Cinematic";
import { BrandFooter } from "../components/brand/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// P3 — Auto-save draft key. Bump suffix to invalidate older drafts.
const DRAFT_KEY = "omertabella.apply_draft_v1";
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return parsed;
  } catch (err) {
    // Corrupt JSON or unavailable storage (private mode, quota) → treat as no draft.
    console.warn("Apply.loadDraft: ignoring corrupt draft", err);
    return null;
  }
}

// NOTE: localStorage here is for the public application form DRAFT only —
// stage_name, social_handles, etc. Nothing sensitive (no tokens, no session ids,
// no PII beyond what the user is about to POST publicly anyway). Do NOT add
// auth material or anything secret to this object.
function saveDraft(form) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, savedAt: Date.now() }));
  } catch (err) {
    // Storage quota exceeded or disabled (private mode). Non-fatal — auto-save just no-ops.
    console.warn("Apply.saveDraft: localStorage unavailable", err);
  }
}

function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); }
  catch (err) { console.warn("Apply.clearDraft: localStorage unavailable", err); }
}

const REVENUE   = ["$0-1K","$1K-5K","$5K-10K","$10K-25K","$25K-50K","$50K+","Prefer not to say"];
const TIME_ON   = ["<3 mo","3-6 mo","6-12 mo","1-2 yr","2+ yr","Prefer not to say"];
const PLATFORMS = ["Instagram","TikTok","Reddit","X","Snapchat","YouTube"]; // legacy — kept for backwards compat (active_platforms still optional in payload)
// eslint-disable-next-line no-unused-vars
const _PLATFORMS_LEGACY_REF = PLATFORMS;
const STRUGGLES = ["Growth","Content","Messaging","Marketing","Pricing","All of it","Prefer not to say"];
const HEARD     = ["Friend","Instagram","TikTok","Twitter","Reddit","Search","Other","Prefer not to say"];

// Generates a stable per-row id for social_handles rows. Used as React `key`
// so re-ordering / removing rows doesn't reuse the wrong input element.
function makeRowId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `row_${crypto.randomUUID()}`;
  }
  return `row_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Floating-label input. Label slides up + shrinks when input has value or focus.
 *  `example` is a small non-blocking helper shown below the input (e.g. "e.g. Jane A. Smith"). */
function FloatInput({ label, testid, value, onChange, type = "text", error, helper, example, ...rest }) {
  const filled = value && String(value).length > 0;
  return (
    <label className="group relative block">
      <input
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testid}
        placeholder=" "
        className={`peer block w-full min-h-[52px] rounded-2xl border bg-brand-bg/40 px-5 pt-6 pb-2 text-sm text-brand-text outline-none transition-all
                    ${error ? "border-brand-danger/60" : "border-brand-text/15"}
                    focus:border-brand-gold/60 focus:bg-brand-bg/60 focus:shadow-[0_0_0_3px_rgba(201,168,76,0.12)]`}
        {...rest}
      />
      <span className={`pointer-events-none absolute left-5 font-mono text-[10px] uppercase tracking-[0.32em] transition-all
                       ${filled ? "top-2 text-brand-gold/80" : "top-1/2 -translate-y-1/2 text-brand-text/40 peer-focus:top-2 peer-focus:translate-y-0 peer-focus:text-brand-gold/80 peer-focus:text-[10px]"}`}>
        {label}
      </span>
      {(helper || example) && !error && (
        <p data-testid={testid ? `${testid}-helper` : undefined} className="mt-1.5 px-1 text-[11px] leading-relaxed text-brand-text/50">
          {helper}{helper && example ? " · " : ""}{example && <span className="italic text-brand-text/40">{example}</span>}
        </p>
      )}
      {error && <p className="mt-1.5 px-1 text-[11px] text-brand-danger/85">{error}</p>}
    </label>
  );
}

/** Floating-label textarea. Character counter shown when `max` is provided. */
function FloatTextarea({ label, testid, value, onChange, rows = 4, helper, example, max, ...rest }) {
  const filled = value && String(value).length > 0;
  const len = (value || "").length;
  return (
    <label className="group relative block">
      <textarea
        rows={rows} value={value} onChange={onChange} data-testid={testid} placeholder=" "
        maxLength={max || undefined}
        className="peer block w-full resize-none rounded-2xl border border-brand-text/15 bg-brand-bg/40 px-5 pt-7 pb-3 text-sm text-brand-text outline-none transition-all focus:border-brand-gold/60 focus:bg-brand-bg/60 focus:shadow-[0_0_0_3px_rgba(201,168,76,0.12)]"
        {...rest}
      />
      <span className={`pointer-events-none absolute left-5 font-mono text-[10px] uppercase tracking-[0.32em] transition-all
                       ${filled ? "top-2 text-brand-gold/80" : "top-4 text-brand-text/40 peer-focus:top-2 peer-focus:text-brand-gold/80"}`}>
        {label}
      </span>
      <div className="mt-1.5 flex items-start justify-between gap-3 px-1">
        {(helper || example) ? (
          <p data-testid={testid ? `${testid}-helper` : undefined} className="text-[11px] leading-relaxed text-brand-text/50">
            {helper}{helper && example ? " · " : ""}{example && <span className="italic text-brand-text/40">{example}</span>}
          </p>
        ) : <span />}
        {max && (
          <span data-testid={testid ? `${testid}-counter` : undefined} className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.24em] ${len > max ? "text-brand-danger" : "text-brand-text/35"}`}>
            {len} / {max}
          </span>
        )}
      </div>
    </label>
  );
}

function Pill({ active, onClick, testid, children }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
            className={`min-h-[44px] rounded-full border px-4 py-3 text-xs uppercase tracking-[0.18em] transition-all
                        ${active
                          ? "border-brand-gold bg-brand-gold/15 text-brand-gold shadow-[0_0_24px_-8px_rgba(201,168,76,0.45)]"
                          : "border-brand-text/15 bg-brand-bg/30 text-brand-text/65 hover:border-brand-gold/40 hover:text-brand-text"}`}>
      {children}
    </button>
  );
}

/** Small helper caption used above pill groups (e.g. below a required-question label). */
function FieldHelper({ testid, children }) {
  return (
    <p data-testid={testid} className="mt-1 text-[11px] leading-relaxed text-brand-text/50">{children}</p>
  );
}

function Section({ index, title, recap, children }) {
  return (
    <section className="mt-16">
      <Reveal><Eyebrow index={index}>{title}</Eyebrow></Reveal>
      <DrawRule className="mt-4" width="w-10" />
      {recap && (
        <p data-testid={`apply-section-recap-${index}`} className="mt-4 max-w-xl text-sm leading-relaxed text-brand-text/60">
          {recap}
        </p>
      )}
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-text/40">
        <span className="text-brand-gold">*</span> required
      </p>
      <div className="mt-8 space-y-6">{children}</div>
    </section>
  );
}

export default function Apply() {
  const navigate = useNavigate();
  useEffect(() => { document.title = "Apply · Omertàbella"; }, []);
  const [form, setForm] = useState({
    legal_name: "", stage_name: "", platform_username: "", email: "", phone: "",
    platforms: [],
    // Structured social handles — replaces the old "Active Platforms" pill grid.
    // Start with 2 empty rows; the user picks platforms from the dropdown.
    // Each row carries a stable `rid` (row id) so React can key on it instead of index —
    // index keys break re-ordering & remove animations when rows shift.
    social_handles: [
      { rid: makeRowId(), platform: "", handle: "" },
      { rid: makeRowId(), platform: "", handle: "" },
    ],
    onlyfans_handle: "",
    current_monthly_revenue: "", time_on_platform: "", active_subscribers: "",
    active_platforms: [], biggest_struggle: "", heard_about: "", additional_info: "",
    agrees_to_discretion: true,
    // P2.5 — Honeypot. Real users never see/fill this; bots auto-fill anything with `url`/`website`.
    website_url: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // P3 — Resume-draft banner state. Shown once on mount if a recent draft exists.
  const [resumePrompt, setResumePrompt] = useState(() => {
    const d = loadDraft();
    return d ? { savedAt: d.savedAt, form: d.form } : null;
  });

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };
  // P3 — Persist on every blur. Cheap and safe.
  const persist = () => { saveDraft(form); };
  // Legacy `active_platforms` toggler — retained so the eslint/no-unused-vars rule stays quiet;
  // the new structured `social_handles` UI below has replaced its render.
  // eslint-disable-next-line no-unused-vars
  const togglePlatform = (p) => set("active_platforms",
    form.active_platforms.includes(p) ? form.active_platforms.filter(x=>x!==p) : [...form.active_platforms, p]);
  const togglePlatformIntent = (p) => set("platforms",
    form.platforms.includes(p) ? form.platforms.filter(x=>x!==p) : [...form.platforms, p]);

  // Social handles helpers (Task — /apply social handles overhaul)
  const SOCIAL_PLATFORM_OPTIONS = [
    "Instagram", "TikTok", "YouTube", "X", "Twitch", "Kick",
    "Threads", "Snapchat", "Reddit", "Facebook", "Pinterest", "Other",
  ];
  const updateSocialRow = (idx, key, val) => {
    setForm((f) => {
      const next = f.social_handles.map((r, i) => (i === idx ? { ...r, [key]: val } : r));
      return { ...f, social_handles: next };
    });
    setErrors((e) => ({ ...e, social_handles: undefined }));
  };
  const addSocialRow = () => {
    setForm((f) => {
      if (f.social_handles.length >= 8) return f;
      return { ...f, social_handles: [...f.social_handles, { rid: makeRowId(), platform: "", handle: "" }] };
    });
  };
  const removeSocialRow = (idx) => {
    setForm((f) => {
      const next = f.social_handles.filter((_, i) => i !== idx);
      return { ...f, social_handles: next.length ? next : [{ rid: makeRowId(), platform: "", handle: "" }] };
    });
  };

  const resumeFromDraft = () => {
    if (!resumePrompt?.form) return;
    setForm((f) => ({ ...f, ...resumePrompt.form, website_url: "" })); // never restore the honeypot
    setResumePrompt(null);
  };
  const dismissDraft = () => { clearDraft(); setResumePrompt(null); };

  const computeErrors = () => {
    const e = {};
    if (form.legal_name.trim().length < 2) e.legal_name = "Required";
    if (form.stage_name.trim().length < 2) e.stage_name = "Required";
    if (form.platform_username.trim().length < 2) e.platform_username = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    if (!form.platforms || form.platforms.length === 0) e.platforms = "Select at least one";
    if (!form.current_monthly_revenue) e.current_monthly_revenue = "Select one";
    if (!form.time_on_platform) e.time_on_platform = "Select one";
    if (!form.biggest_struggle) e.biggest_struggle = "Select one";
    if (!form.heard_about) e.heard_about = "Select one";
    // Social handles — at least one row with both platform + handle filled in.
    const validRows = (form.social_handles || []).filter(
      (r) => r && (r.platform || "").trim() && (r.handle || "").trim()
    );
    if (validRows.length === 0) e.social_handles = "Add at least one social handle";
    return e;
  };
  const validate = () => {
    const e = computeErrors();
    setErrors(e); return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      // P0-2 fix — do NOT scroll to top (that shows the hero, not the errors).
      // Instead: scroll the summary banner into view, then focus the first invalid field.
      // Field order matches the visual top-to-bottom order in the form.
      const FIELD_ORDER = [
        ["legal_name",              "apply-legal-name"],
        ["stage_name",              "apply-stage-name"],
        ["platform_username",       "apply-platform-username"],
        ["email",                   "apply-email"],
        ["platforms",               "apply-platform-intent-onlyfans_only"],
        ["current_monthly_revenue", "apply-revenue-$0-1K"],
        ["time_on_platform",        "apply-time-<3 mo"],
        ["social_handles",          "apply-social-handles-block"],
        ["biggest_struggle",        "apply-struggle-Growth"],
        ["heard_about",             "apply-heard-Friend"],
      ];
      const fresh = computeErrors();
      // Defer to next tick so the banner has rendered before we scroll to it.
      setTimeout(() => {
        const banner = document.querySelector('[data-testid="apply-form-errors-summary"]');
        if (banner) banner.scrollIntoView({ behavior: "smooth", block: "start" });
        for (const [key, testid] of FIELD_ORDER) {
          if (fresh[key]) {
            const el = document.querySelector(`[data-testid="${testid}"]`);
            if (el) {
              if (typeof el.focus === "function") { try { el.focus({ preventScroll: true }); } catch { el.focus(); } }
              if (!banner) el.scrollIntoView({ behavior: "smooth", block: "center" });
              break;
            }
          }
        }
      }, 0);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        active_subscribers: form.active_subscribers === "" ? null : parseInt(form.active_subscribers, 10) || null,
        phone: form.phone || null,
        additional_info: form.additional_info || null,
        // Strip empty social_handles rows before sending; backend requires >= 1.
        social_handles: (form.social_handles || [])
          .filter((r) => r && (r.platform || "").trim() && (r.handle || "").trim())
          .map((r) => ({ platform: r.platform, handle: r.handle.trim() })),
        onlyfans_handle: (form.onlyfans_handle || "").trim() || null,
      };
      const { data } = await axios.post(`${API}/applications`, payload);
      clearDraft(); // P3 — wipe the localStorage draft on successful submit
      try { sessionStorage.setItem("omb.last_application_ref", data.application_id); }
      catch (err) { console.warn("Apply: sessionStorage unavailable", err); }
      navigate(`/applied?ref=${encodeURIComponent(data.application_id)}`, { replace: true, state: { ref: data.application_id } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const flat = {}; detail.forEach(d => { flat[d.loc?.[d.loc.length-1]] = d.msg; }); setErrors(flat);
      } else {
        setErrors({ form: detail || "Submission failed. Please try again." });
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally { setSubmitting(false); }
  };

  return (
    <main data-testid="apply-page" className="relative min-h-screen bg-brand-bg text-brand-text-warm">
      <CinematicCanvas />
      <MonogramWatermark className="left-[-25%] top-[5%]" />

      <header className="relative z-20 mx-auto flex max-w-3xl items-center justify-between px-6 pt-8 sm:px-10">
        <Link to="/" data-testid="apply-back-link"
              className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/50 transition-colors hover:text-brand-gold">
          ← Back
        </Link>
        <LogoLockup variant="header" as={Link} to="/" />
        <span className="w-12" />
      </header>

      <form onSubmit={submit} onBlur={persist} className="relative z-10 mx-auto max-w-3xl px-6 pb-32 pt-16 sm:px-10">
        {/* P2.5 — Honeypot. Off-screen via position+opacity (NOT display:none which some bots detect).
            P0-3 hardening: add autoComplete + data-lpignore + data-1p-ignore + data-form-type=other
            so password managers (LastPass, 1Password, browser autofill, aggressive form fillers)
            do NOT populate this field. Password-manager autofill was silently triggering the
            honeypot for accessibility users → fake-201 → confused user on /status. Bots ignore
            these attributes and still get caught server-side. */}
        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: 0, height: 1, width: 1, overflow: "hidden", opacity: 0 }}>
          <label>
            Website URL (leave blank)
            <input
              type="text"
              name="website_url"
              tabIndex={-1}
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              aria-hidden="true"
              value={form.website_url}
              onChange={e => set("website_url", e.target.value)}
              data-testid="apply-honeypot"
            />
          </label>
        </div>
        {/* P3 — Resume-draft banner. Shown once on mount if a draft <7d old exists. */}
        {resumePrompt && (
          <div
            data-testid="apply-resume-banner"
            className="mb-8 flex flex-col gap-3 rounded-2xl border border-brand-gold/40 bg-brand-gold/[0.06] p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold">Resume your application?</p>
              <p className="mt-1 text-sm text-brand-text/75">
                We saved your work from {new Date(resumePrompt.savedAt).toLocaleString()}.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="apply-resume-yes"
                onClick={resumeFromDraft}
                className="rounded-full border border-brand-gold bg-brand-gold/15 px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-brand-gold hover:bg-brand-gold hover:text-brand-bg"
              >
                Resume
              </button>
              <button
                type="button"
                data-testid="apply-resume-no"
                onClick={dismissDraft}
                className="rounded-full border border-brand-text/20 px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-brand-text/60 hover:border-brand-text/40 hover:text-brand-text"
              >
                Start fresh
              </button>
            </div>
          </div>
        )}
        <Reveal>
          <div className="text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.42em] text-brand-gold">· Invitation Request ·</div>
            <h1 className="mt-8 font-display font-normal text-brand-text text-balance"
                style={{ fontSize: "clamp(40px, 6vw, 88px)", letterSpacing: "-0.035em", lineHeight: 1.04 }}>
              Request a <em className="italic text-brand-gold">private</em> review.
            </h1>
            <DrawRule className="mx-auto mt-6" width="w-12" />
            <p className="mx-auto mt-8 max-w-lg text-sm leading-relaxed text-brand-text/65 sm:text-base">
              Submissions are read by leadership only and held in strict confidence. A response, if extended, arrives within forty-eight hours.
            </p>
          </div>
        </Reveal>

        {errors.form && (
          <p data-testid="apply-form-error"
             className="mt-10 rounded-2xl border border-brand-danger/40 bg-brand-danger/10 p-5 text-sm text-brand-text" role="alert">
            {errors.form}
          </p>
        )}
        {/* P0-2 fix — client-side error summary banner. Renders whenever validate() sets any
            field-level error. Lists the first 3 missing fields so the user knows what to fix
            without hunting through the whole form. */}
        {(() => {
          const FIELD_LABELS = {
            legal_name: "Legal Name",
            stage_name: "Stage Name",
            platform_username: "Platform Username",
            email: "Email",
            platforms: "Working platform choice",
            current_monthly_revenue: "Current Monthly Revenue",
            time_on_platform: "Time on Platform",
            social_handles: "At least one Social handle row",
            biggest_struggle: "What are you struggling with?",
            heard_about: "How did you hear about us?",
          };
          const missing = Object.keys(errors).filter((k) => k !== "form" && errors[k] && FIELD_LABELS[k]);
          if (missing.length === 0) return null;
          const shown = missing.slice(0, 3).map((k) => FIELD_LABELS[k]);
          const extra = missing.length > 3 ? ` (and ${missing.length - 3} more)` : "";
          return (
            <div
              data-testid="apply-form-errors-summary"
              role="alert"
              aria-live="polite"
              className="mt-10 rounded-2xl border border-brand-danger/45 bg-brand-danger/[0.08] p-5"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-danger/90">
                Please complete the required fields
              </p>
              <p className="mt-2 text-sm text-brand-text/85">
                {shown.join(", ")}{extra}.
              </p>
            </div>
          );
        })()}

        <Reveal delay={0.15}>
          <motion.div className="glass relative mt-14 overflow-hidden rounded-3xl p-7 sm:p-10">
            <CornerBrackets size={20} />

            <Section index="01" title="Identity" recap="The basics we use to introduce you to leadership. Kept confidential — no marketing lists, ever.">
              <div className="grid gap-5 sm:grid-cols-2">
                <FloatInput label="Legal Name *"        testid="apply-legal-name"        value={form.legal_name}        onChange={e=>set("legal_name", e.target.value)}        error={errors.legal_name}        helper="As it appears on your government ID."        example="e.g. Jane A. Smith" />
                <FloatInput label="Stage Name *"        testid="apply-stage-name"        value={form.stage_name}        onChange={e=>set("stage_name", e.target.value)}        error={errors.stage_name}        helper="The name your audience knows you by."   example="e.g. QueenBee" />
                <FloatInput label="Platform Username *" testid="apply-platform-username" value={form.platform_username} onChange={e=>set("platform_username", e.target.value)} error={errors.platform_username} helper="Your handle without the @."               example="e.g. queenbee" />
                <FloatInput label="Email *"             testid="apply-email"             value={form.email}             onChange={e=>set("email", e.target.value)}             error={errors.email} type="email"     helper="Where we send status updates."           example="e.g. you@yourdomain.com" />
                <div className="sm:col-span-2 rounded-2xl border border-brand-gold/25 bg-brand-gold/[0.04] px-4 py-3">
                  <p data-testid="apply-email-permanent-note" className="text-[11px] leading-relaxed text-brand-text/65">
                    <span className="font-mono uppercase tracking-[0.22em] text-brand-gold/80">Note</span> · We only accept applications with permanent email addresses. Temporary and disposable inboxes (mailinator, 10minutemail, etc.) will be rejected.
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <FloatInput label="Phone (optional)" testid="apply-phone" value={form.phone} onChange={e=>set("phone", e.target.value)} helper="Optional. Used only if we need to reach you quickly." example="e.g. +1 555 123 4567" />
                </div>
              </div>
            </Section>

            <Section index="02" title="Working Platforms" recap="Where you plan to focus. Shapes which questions we ask you later — pick the closest match.">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/55">Which platforms do you want to work on? <span className="text-brand-gold">*</span></p>
                <FieldHelper testid="apply-platforms-helper">Pick the mix that matches your creator strategy. You can adjust later.</FieldHelper>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    {key: "onlyfans_only", label: "OnlyFans only"},
                    {key: "tiktok_only", label: "TikTok only (live)"},
                    {key: "onlyfans_plus_tiktok", label: "OnlyFans + TikTok Live"},
                  ].map(o => (
                    <Pill
                      key={o.key}
                      testid={`apply-platform-intent-${o.key}`}
                      active={form.platforms.includes(o.key)}
                      onClick={() => togglePlatformIntent(o.key)}
                    >{o.label}</Pill>
                  ))}
                </div>
                {errors.platforms && <p data-testid="apply-platforms-error" className="mt-2 text-xs text-brand-danger/85">{errors.platforms}</p>}
              </div>
            </Section>

            <Section index="03" title="The Numbers" recap="Approximate ranges only — no exact figures needed. Ballpark helps us match you with the right manager.">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/55">Current Monthly Revenue <span className="text-brand-gold">*</span></p>
                <FieldHelper testid="apply-revenue-helper">Pick a range. If revenue is variable, use the average of the last three months.</FieldHelper>
                <div className="mt-3 flex flex-wrap gap-2">
                  {REVENUE.map(r => <Pill key={r} testid={`apply-revenue-${r}`} active={form.current_monthly_revenue===r} onClick={()=>set("current_monthly_revenue", r)}>{r}</Pill>)}
                </div>
                {errors.current_monthly_revenue && <p className="mt-2 text-xs text-brand-danger/85">{errors.current_monthly_revenue}</p>}
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/55">Time on Platform <span className="text-brand-gold">*</span></p>
                <FieldHelper testid="apply-time-helper">How long you've been actively creating on your primary platform.</FieldHelper>
                <div className="mt-3 flex flex-wrap gap-2">
                  {TIME_ON.map(t => <Pill key={t} testid={`apply-time-${t}`} active={form.time_on_platform===t} onClick={()=>set("time_on_platform", t)}>{t}</Pill>)}
                </div>
                {errors.time_on_platform && <p className="mt-2 text-xs text-brand-danger/85">{errors.time_on_platform}</p>}
              </div>
              <FloatInput label="Approximate Active Subscribers" testid="apply-subscribers" type="number" min="0" value={form.active_subscribers} onChange={e=>set("active_subscribers", e.target.value)} helper="Optional. Rough count of paying subs." example="e.g. 1200" />
            </Section>

            <Section index="04" title="Your Reach" recap="Where you build audience and what you're hoping we help with first.">
              <div data-testid="apply-social-handles-block">
                <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/55">Social handles <span className="text-brand-gold">*</span></p>
                <FieldHelper testid="apply-social-helper">Add one row per platform. We use these to verify your reach — even small accounts help.</FieldHelper>
                <p className="mt-1 text-xs text-brand-text/55">Add one row per platform. We use these to verify your reach.</p>
                <div className="mt-3 space-y-2">
                  {form.social_handles.map((row, idx) => (
                    <div
                      key={row.rid || `row-${idx}`}
                      data-testid={`apply-social-row-${idx}`}
                      className="grid grid-cols-1 gap-2 sm:grid-cols-[160px_1fr_auto] sm:items-center"
                    >
                      <select
                        data-testid={`apply-social-platform-${idx}`}
                        value={row.platform}
                        onChange={(e) => updateSocialRow(idx, "platform", e.target.value)}
                        className="min-h-[44px] rounded-md border border-brand-text/15 bg-brand-bg/60 px-3 py-2.5 text-sm text-brand-text focus:border-brand-gold focus:outline-none"
                      >
                        <option value="" disabled>Platform</option>
                        {SOCIAL_PLATFORM_OPTIONS.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                      <input
                        data-testid={`apply-social-handle-${idx}`}
                        type="text"
                        value={row.handle}
                        onChange={(e) => updateSocialRow(idx, "handle", e.target.value)}
                        placeholder="@handle or URL"
                        maxLength={200}
                        className="min-h-[44px] rounded-md border border-brand-text/15 bg-brand-bg/60 px-3 py-2.5 text-sm text-brand-text placeholder:text-brand-text/35 focus:border-brand-gold focus:outline-none"
                      />
                      <button
                        type="button"
                        data-testid={`apply-social-remove-${idx}`}
                        onClick={() => removeSocialRow(idx)}
                        disabled={form.social_handles.length === 1}
                        aria-label="Remove row"
                        className="min-h-[44px] rounded-md border border-brand-text/15 px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-brand-text/60 hover:border-brand-danger/50 hover:text-brand-danger disabled:opacity-30"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <button
                    type="button"
                    data-testid="apply-social-add"
                    onClick={addSocialRow}
                    disabled={form.social_handles.length >= 8}
                    className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold transition-colors hover:text-brand-gold/70 disabled:opacity-30"
                  >
                    + Add another platform
                  </button>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-text/40">
                    {form.social_handles.length} / 8
                  </span>
                </div>
                {errors.social_handles && (
                  <p data-testid="apply-social-error" className="mt-2 text-xs text-brand-danger/85">
                    {errors.social_handles}
                  </p>
                )}

                {/* OnlyFans handle — separate, optional, kept discreet */}
                <div className="mt-6 rounded-2xl border border-brand-gold/20 bg-brand-gold/[0.04] p-4">
                  <label className="block">
                    <span className="font-mono text-[11px] italic tracking-wider text-brand-gold/85">
                      OnlyFans handle (optional — keep discreet)
                    </span>
                    <input
                      data-testid="apply-onlyfans-handle"
                      type="text"
                      maxLength={120}
                      value={form.onlyfans_handle}
                      onChange={(e) => set("onlyfans_handle", e.target.value)}
                      placeholder="onlyfans.com/yourname"
                      className="mt-2 w-full rounded-md border border-brand-text/15 bg-brand-bg/70 px-3 py-2 text-sm text-brand-text placeholder:text-brand-text/35 focus:border-brand-gold focus:outline-none"
                    />
                  </label>
                  <p className="mt-2 text-[11px] text-brand-text/55">
                    Only Omertàbella leadership will see this.
                  </p>
                </div>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/55">What are you struggling with most? <span className="text-brand-gold">*</span></p>
                <FieldHelper testid="apply-struggle-helper">What one area needs the most attention right now?</FieldHelper>
                <div className="mt-3 flex flex-wrap gap-2">
                  {STRUGGLES.map(s => <Pill key={s} testid={`apply-struggle-${s}`} active={form.biggest_struggle===s} onClick={()=>set("biggest_struggle", s)}>{s}</Pill>)}
                </div>
                {errors.biggest_struggle && <p className="mt-2 text-xs text-brand-danger/85">{errors.biggest_struggle}</p>}
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/55">How did you hear about us? <span className="text-brand-gold">*</span></p>
                <FieldHelper testid="apply-heard-helper">Helps us understand where our best creators come from.</FieldHelper>
                <div className="mt-3 flex flex-wrap gap-2">
                  {HEARD.map(h => <Pill key={h} testid={`apply-heard-${h}`} active={form.heard_about===h} onClick={()=>set("heard_about", h)}>{h}</Pill>)}
                </div>
                {errors.heard_about && <p className="mt-2 text-xs text-brand-danger/85">{errors.heard_about}</p>}
              </div>
              <FloatTextarea label="Anything else we should know?" testid="apply-additional" rows={4} max={2000} value={form.additional_info} onChange={e=>set("additional_info", e.target.value)} helper="Optional. Context, questions, or anything we should know before we review." />
            </Section>
          </motion.div>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="mt-14 flex flex-col items-center gap-5">
            <Button data-testid="apply-submit" type="submit" size="lg" disabled={submitting} className="w-full sm:w-auto sm:min-w-[320px]">
              {submitting ? "Submitting…" : "Submit Application"}
            </Button>
            <p className="max-w-xs text-center font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/35">
              By submitting you agree to our discretion policy. All information stays inside Omertàbella.
            </p>
          </div>
        </Reveal>
      </form>
      <BrandFooter />
    </main>
  );
}
